import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TenantContextService, formatInTenantZone } from 'shared/core';
import { TallyMasterSyncKind, TallyMasterSyncRunModel } from '../../Models/TallyModel';
import { TallyService } from '../../Services/tally.service';

/** How often an in-flight refresh is re-read. */
export const TALLY_SYNC_POLL_MS = 5000;

/** The line under a refresh button, worked out once per read rather than per change-detection pass. */
export interface TallySyncRunView {
  tone: 'active' | 'done' | 'failed';
  icon: string;
  text: string;
  /** The full reason or detail; the line itself clamps. */
  tooltip: string;
  isActive: boolean;
}

/**
 * The progress of the latest Tally master refresh (ledgers, godowns or stock items),
 * shown beside its refresh button. A refresh runs on the Tally machine through the
 * agent, so it can wait, run for a while, or fail there; this line says which. It
 * polls while the refresh is in flight and tells the page when one completes, so the
 * page re-reads its list once instead of guessing when the data has landed.
 *
 * Standalone so the supplier, department and store pages can all use it without
 * sharing a module.
 */
@Component({
  selector: 'app-tally-sync-run-status',
  standalone: true,
  imports: [NzIconModule, NzTooltipModule],
  template: `
    @if (view) {
      <div class="tally-sync-run" [class]="'tally-sync-run tally-sync-run--' + view.tone"
        nz-tooltip [nzTooltipTitle]="view.tooltip">
        <span nz-icon [nzType]="view.icon" [nzSpin]="view.isActive" nzTheme="outline"></span>
        <span class="tally-sync-run__text">{{ view.text }}</span>
      </div>
    }
  `,
  styles: [`
    .tally-sync-run { margin-top: 4px; font-size: 12px; line-height: 1.4; display: flex; gap: 6px; align-items: flex-start; cursor: help; }
    .tally-sync-run__text { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .tally-sync-run--active { color: #1677ff; }
    .tally-sync-run--done { color: rgba(0, 0, 0, 0.55); }
    .tally-sync-run--failed { color: #d4380d; }
  `]
})
export class TallySyncRunStatusComponent implements OnInit, OnDestroy {
  @Input({ required: true }) kind: TallyMasterSyncKind;

  /** A refresh this line was following has just completed: re-read the list. */
  @Output() completed = new EventEmitter<void>();

  view: TallySyncRunView | null = null;

  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private request: Subscription | null = null;
  private wasActive = false;
  private destroyed = false;

  private readonly tenantContext = inject(TenantContextService);

  constructor(private tallyService: TallyService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stop();
  }

  /** Call after requesting a refresh (or being refused one): shows it at once and follows it to the end. */
  watch(): void {
    this.stop();
    this.load();
  }

  /** One chain of reads at a time: a pending timer and an in-flight read both end here. */
  private stop(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.request?.unsubscribe();
    this.request = null;
  }

  private schedule(): void {
    if (this.destroyed) {
      return;
    }
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      this.load();
    }, TALLY_SYNC_POLL_MS);
  }

  private load(): void {
    this.request = this.tallyService.getMasterSyncRuns(this.kind, 1).subscribe({
      next: runs => {
        this.request = null;
        this.apply(runs?.[0] ?? null);
      },
      error: (err: HttpErrorResponse) => {
        this.request = null;
        if (err?.status === 403 || err?.status === 404) {
          // Not this user's to see, or an older backend: no line, as before.
          this.view = null;
          return;
        }
        // A passing network or server error: keep what is shown and try again.
        if (this.wasActive) {
          this.schedule();
        }
      }
    });
  }

  private apply(run: TallyMasterSyncRunModel | null): void {
    if (this.destroyed) {
      return;
    }
    // The company's own time zone, matching the Tally Sync History dialog beside it.
    const timeZone = this.tenantContext.timeZone();
    this.view = TallySyncRunStatusComponent.describe(run, value => formatInTenantZone(value, timeZone, 'datetime'));
    if (run?.IsActive) {
      this.wasActive = true;
      this.schedule();
      return;
    }
    if (this.wasActive && run?.Status === 'Completed') {
      this.completed.emit();
    }
    this.wasActive = false;
  }

  /** The line for the latest run; null when there has never been one. */
  static describe(run: TallyMasterSyncRunModel | null, toTime: (value: string | null) => string): TallySyncRunView | null {
    if (!run) {
      return null;
    }
    const who = run.RequestedByName ? ` by ${run.RequestedByName}` : '';
    if (run.IsActive) {
      const since = toTime(run.DispatchedUtc ?? run.RequestedUtc);
      const text = run.Status === 'Dispatched'
        ? `Reading from Tally… (since ${since})`
        : `Waiting for the Tally agent to pick this up… (requested ${since})`;
      return { tone: 'active', icon: 'sync', text, tooltip: run.Detail || `Refresh requested${who}.`, isActive: true };
    }
    if (run.Status === 'Completed') {
      const count = run.ItemCount != null ? ` · ${run.ItemCount.toLocaleString('en-IN')} ${TallySyncRunStatusComponent.noun(run.SyncKind)}` : '';
      return {
        tone: 'done',
        icon: 'check-circle',
        text: `Last refreshed from Tally ${toTime(run.CompletedUtc)}${count}`,
        tooltip: `Requested${who}. ${run.Detail ?? ''}`.trim(),
        isActive: false
      };
    }
    const reason = run.Detail || 'No reason was reported.';
    const heading = run.DisplayStatus === 'No answer' ? 'No answer from Tally' : 'Last refresh failed';
    return {
      tone: 'failed',
      icon: 'warning',
      text: `${heading} (${toTime(run.CompletedUtc ?? run.DispatchedUtc ?? run.RequestedUtc)}): ${reason}`,
      tooltip: reason,
      isActive: false
    };
  }

  private static noun(kind: TallyMasterSyncKind): string {
    switch (kind) {
      case 'Ledgers': return 'ledgers';
      case 'Godowns': return 'godowns';
      default: return 'stock items';
    }
  }
}
